import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useModal } from '../../context/ModalContext';
import { useLoading } from '../../context/LoadingContext';
import SideBar from './Conversations/SideBar/SideBar';
import ChatView from './Conversations/ChatView/ChatView';
import NewChatView from './Conversations/ChatView/NewChatView';
import { useDragResize } from '../../hooks/useDragResize';
import { useSortedConversations } from '../../hooks/useSorting';
import { useChannels } from '../../hooks/useChannels';
import { useLayout } from '../../context/LayoutContext';
import { useNavigation } from '../../context/NavigationContext';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import useLastViewedRoom from '../../hooks/useLastViewedRoom';
import useFileAttachment from '../../hooks/useFileAttachment';
import { useFileDragDrop } from '../../hooks/useFileDragDrop';
import useMessageQueue from '../../hooks/useMessageQueue';
import { useAvatar } from '../../context/AvatarContext';
import DragDropOverlay from '../DragDropOverlay/DragDropOverlay';
import logger from '../../utils/logger';
import './ChannelsPage.css';

const ChannelsPage = () => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { showError } = useModal();
  const { sideBarWidth, resizeSideBar, toggleMenu } = useLayout();
  const { showLoading, hideLoading } = useLoading();

  // Track window width to auto-hide sidebar below 700px
  const isWideEnoughForSidebars = useWindowWidth(700);

  const { onMouseDown: onResizeBarMouseDown } = useDragResize({
    onResizeX: resizeSideBar,
    getCurrentX: () => sideBarWidth,
    enableX: true,
    enableY: false,
  });

  const { channelList, isLoading, createChannel, isChannelOwner } = useChannels();
  const { navigateToChannel, isCreatingNew, setNavigationGuard } = useNavigation();
  const { setRoomAvatar } = useAvatar();
  const sortedChannels = useSortedConversations(channelList);
  const [isEditing, setIsEditing] = useState(false);
  const [editFocusTarget, setEditFocusTarget] = useState('topic'); // 'topic' or 'participants'
  const { setLastViewed } = useLastViewedRoom('channels');
  
  // Track if we just created a channel to prevent auto-redirect
  const justCreatedChannelRef = useRef(null);

  // Extract messageId from query params for deep-linking to specific messages
  const messageIdToFocus = searchParams.get('messageId');

  // Callback to clear the messageId query param after focusing
  const handleMessageFocused = useCallback(() => {
    if (messageIdToFocus) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('messageId');
        return newParams;
      }, { replace: true });
    }
  }, [messageIdToFocus, setSearchParams]);

  const currentId = useMemo(() => {
    if (!params.id) return null;
    // Decode the ID from the URL (Matrix room IDs are URL-encoded)
    return params.id === 'new' ? null : decodeURIComponent(params.id);
  }, [params.id]);

  // Security: alert the user when an attachment is rejected for its file type.
  const handleFileRejected = useCallback((file) => {
    showError(
      t('AttachmentValidation.blockedFileTypeTitle'),
      t('AttachmentValidation.blockedFileTypeMessage', { fileName: file?.name || '' }),
    );
  }, [showError, t]);

  // File attachment management
  const {
    attachment,
    isUploading,
    fileInputRef,
    uploadProgress,
    handleStartUpload,
    handleStopUpload,
    handleCancelUpload,
    handleClearAttachment,
    handleFileSelect,
    handleTriggerFileSelect,
    setUploadProgress,
  } = useFileAttachment({
    clearOnRoomChange: true,
    currentRoomId: currentId,
    onFileRejected: handleFileRejected,
  });
  const { isBusy: isQueueBusy } = useMessageQueue(isCreatingNew ? null : currentId);

  // Validate if the ID from the URL is a real channel
  const channelExists = currentId && sortedChannels.some(channel => channel.id === currentId);

  // Determine the default channel to select if the URL is invalid
  const defaultId = useMemo(() => {
    // Always select the first (top) channel in the sorted list
    return sortedChannels[0]?.id || null;
  }, [sortedChannels]);

  // Auto-navigate to default channel if current channel is invalid and not creating new
  useEffect(() => {
    // Don't redirect if we just created this channel (it might not be in the list yet)
    if (currentId && justCreatedChannelRef.current === currentId) {
      return;
    }
    
    if (!isCreatingNew && !channelExists && defaultId) {
      navigateToChannel(defaultId);
    }
  }, [isCreatingNew, channelExists, defaultId, currentId, navigateToChannel]);

  // Track the currently viewed channel
  useEffect(() => {
    if (currentId && channelExists && !isCreatingNew) {
      setLastViewed(currentId);
      // Clear the "just created" flag once the channel exists in the list
      if (justCreatedChannelRef.current === currentId) {
        justCreatedChannelRef.current = null;
      }
    }
  }, [currentId, channelExists, isCreatingNew, setLastViewed]);

  // Set up the navigation guard for creating or editing
  useEffect(() => {
    if (isCreatingNew) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.discardNewConversationErrorTitle'),
        message: t('ChatView.discardNewConversationErrorDesc'),
        confirmText: t('discard'),
        cancelText: t('cancel'),
      });
    } else if (isEditing) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.discardChangesErrorTitle'),
        message: t('ChatView.discardChangesErrorDesc'),
        onConfirm: () => setIsEditing(false), // Reset editing state if user confirms
        confirmText: t('discard'),
        cancelText: t('cancel'),
      });
    } else if (isUploading) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.cancelUploadErrorTitle'),
        message: t('ChatView.cancelUploadErrorDesc'),
        onConfirm: handleCancelUpload,
        confirmText: t('yes'),
        cancelText: t('no'),
      });
    } else {
      // Ensure the guard is inactive if not creating or editing
      setNavigationGuard({ isActive: false });
    }

    // Cleanup function to deactivate the guard when the component unmounts
    return () => setNavigationGuard({ isActive: false });
  }, [isCreatingNew, isEditing, isUploading, setNavigationGuard, handleCancelUpload, t]);

  const handleChannelSelect = useCallback((channelId) => {
    navigateToChannel(channelId);
  }, [navigateToChannel]);

  const handleCreateChannel = useCallback(() => {
    navigateToChannel('new');
  }, [navigateToChannel]);

  const handleCreateChannelSave = useCallback(async (channelData, resetCreatingState) => {
    const {
      title,
      selectedContacts,
      initialMessageText,
      isPublicRoom = false, // Default to private if not specified
      selectedAvatarOption,
      selectedAvatarFile,
      topic,
    } = channelData;

    try {
      showLoading();

      logger.debug('[ChannelsPage] handleCreateChannelSave - START with channelData:', JSON.stringify({
        title,
        selectedContacts,
        initialMessageText,
        hasAvatarFile: !!selectedAvatarFile,
        avatarOption: selectedAvatarOption,
        topic,
      }));

      // For channels: name, topic (use initialMessage as topic), inviteUsers, visibility
      const newChannelId = await createChannel(
        title,
        topic,
        selectedContacts.map(c => c.id),
        isPublicRoom, // Pass public/private setting
        initialMessageText,
      );

      logger.debug('[ChannelsPage] createChannel returned channelId:', newChannelId);

      if (newChannelId) {
        logger.debug('[ChannelsPage] Channel created successfully, handling avatar and navigation');

        // Set channel avatar if custom or preset was selected
        if (selectedAvatarOption === 'custom' && selectedAvatarFile) {
          logger.debug('[ChannelsPage] Setting custom avatar');
          try {
            await setRoomAvatar(newChannelId, selectedAvatarFile);
          } catch (error) {
            logger.error('[ChannelsPage] Failed to set custom avatar:', error);
            showError(
              t('ChannelsPage.avatarUploadFailedTitle'),
              t('ChannelsPage.avatarUploadFailedMessage'),
            );
          }
        } else if (selectedAvatarOption && selectedAvatarOption !== 'default') {
          // Preset avatar - convert path to blob/file
          // Fetch the preset image and upload it
          logger.debug('[ChannelsPage] Setting preset avatar:', selectedAvatarOption);
          try {
            const response = await fetch(selectedAvatarOption);
            const blob = await response.blob();
            const file = new File([blob], 'avatar.png', { type: blob.type });
            await setRoomAvatar(newChannelId, file);
          } catch (error) {
            logger.error('[ChannelsPage] Failed to set preset avatar:', error);
            showError(
              t('ChannelsPage.avatarUploadFailedTitle'),
              t('ChannelsPage.avatarUploadFailedMessage'),
            );
          }
        }

        // Mark this channel as just created to prevent auto-redirect
        justCreatedChannelRef.current = newChannelId;
        logger.debug('[ChannelsPage] Navigating to new channel:', newChannelId);
        // Navigate to the new channel, bypassing the guard for this successful action
        navigateToChannel(newChannelId, { bypassGuard: true });

        hideLoading();
      } else {
        hideLoading();
        logger.error('[ChannelsPage] ERROR - createChannel returned null/undefined channelId');
        showError(
          t('ChannelsPage.createChannelErrorTitle'),
          t('ChannelsPage.createChannelErrorMessage'),
          resetCreatingState, // Reset on modal close
        );
      }
    } catch (error) {
      hideLoading();
      logger.error('[ChannelsPage] Error in handleCreateChannelSave:', error.message);

      // Check if this is a rate limit error
      let errorMessage = t('ChannelsPage.createChannelErrorMessage');
      if (error.httpStatus === 429 || error.errcode === 'M_LIMIT_EXCEEDED') {
        errorMessage = t('ChannelsPage.createChannelRateLimitMessage');
      }

      showError(
        t('ChannelsPage.createChannelErrorTitle'),
        errorMessage,
        resetCreatingState, // Reset on modal close
      );
    }
  }, [createChannel, navigateToChannel, setRoomAvatar, showError, showLoading, hideLoading, t]);

  const handleCreateChannelCancel = useCallback(() => {
    navigateToChannel(null);
  }, [navigateToChannel]);

  const handleLeaveChannel = useCallback(() => {
    // After leaving a channel, navigate back to the channels page
    navigateToChannel(null);
  }, [navigateToChannel]);

  // Drag & Drop for file attachments
  const handleFilesDropped = useCallback((files) => {
    // Simulate file input change event
    const fakeEvent = {
      target: {
        files: files,
      },
    };
    handleFileSelect(fakeEvent);
  }, [handleFileSelect]);

  const { isDraggingOver, dragHandlers } = useFileDragDrop({
    currentRoomId: currentId,
    isCreatingNew,
    isQueueBusy,
    onFilesDropped: handleFilesDropped,
  });

  const renderSideBar = () => {
    // Hide sidebar when screen width < 700px
    if (!isWideEnoughForSidebars) return null;

    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: `${sideBarWidth}px`,
    };

    return (
      <div style={style}>
        <SideBar
          conversations={sortedChannels}
          isCreatingConversation={isCreatingNew}
          selectedConversationId={currentId}
          setCurrentConversationId={handleChannelSelect}
          onCreateConversationClick={handleCreateChannel}
          onMenuToggle={toggleMenu}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const renderResizeBar = () => {
    // Hide resize bar when sidebar is hidden
    if (!isWideEnoughForSidebars) return null;

    const style = {
      position: 'absolute',
      inset: `0px 0px 0px ${sideBarWidth}px`,
      width: '4px',
      zIndex: 1000,
      cursor: 'e-resize',
    };

    return <div style={style} onMouseDown={onResizeBarMouseDown}></div>;
  };

  const renderMainContent = () => {
    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      // Full width when sidebar is hidden by responsive constraint
      width: isWideEnoughForSidebars ? `calc(100% - ${sideBarWidth}px)` : '100%',
    };

    if (isCreatingNew) {
      return (
        <div style={style}>
          <NewChatView 
            onSave={handleCreateChannelSave} 
            onCancel={handleCreateChannelCancel}
            isChannel={true}
          />
        </div>
      );
    }

    if (!channelExists) {
      return <div style={style} />;
    }

    return (
      <div style={style}>
        <ChatView
          roomId={currentId}
          isEditing={isEditing}
          startEdit={(focusTarget = 'topic') => {
            setEditFocusTarget(focusTarget);
            setIsEditing(true);
          }}
          closeEdit={() => setIsEditing(false)}
          editFocusTarget={editFocusTarget}
          startUpload={handleStartUpload}
          stopUpload={handleStopUpload}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          setUploadProgress={setUploadProgress}
          onCancelUpload={handleCancelUpload}
          attachment={attachment}
          onTriggerFileSelect={handleTriggerFileSelect}
          onClearAttachment={handleClearAttachment}
          canEdit={isChannelOwner(currentId)}
          onLeaveRoom={handleLeaveChannel}
          messageIdToFocus={messageIdToFocus}
          onMessageFocused={handleMessageFocused}
        />
      </div>
    );
  };

  return (
    <div 
      className="channels-page"
      {...dragHandlers}
    >
      { renderSideBar() }
      { renderMainContent() }
      { renderResizeBar() }
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {/* Drag & Drop Overlay */}
      <DragDropOverlay 
        text={t('ConversationsPage.dropFileHere')}
        isVisible={isDraggingOver}
      />
    </div>
  );
};

export default ChannelsPage;
